import { Check, ShieldAlert } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, MaskLines, ClipImage } from "@/components/Motion";
import { CallButton, ConsultButton, TextLink } from "@/components/Buttons";
import { CTABand } from "@/components/CTABand";
import { FaqSection } from "@/components/FaqSection";
import { SectionHead, usePageMeta } from "@/components/Common";
import { HURRICANE } from "@/data/site";

const Principles = () => (
  <section data-testid="hurricane-principles" className="bg-white py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <SectionHead eyebrow="A Firefighter's Approach" title={<MaskLines onView lines={["Prepare. Assess.", "Document."]} />}>
        <p>Founder Dwight Saxon spent 30 years with Palm Beach County Fire Rescue. That's the same approach he brings to storm season.</p>
      </SectionHead>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {HURRICANE.principles.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.1}>
            <div data-testid={`hurricane-principle-${i}`} className="h-full border-t-2 border-[#A52A32] bg-[#F8F7F3] p-8">
              <span className="font-mono text-xs text-[#A52A32]">0{i + 1}</span>
              <h3 className="mt-3 font-display text-3xl font-semibold text-[#14263D]">{t}</h3>
              <p className="mt-4 text-base leading-relaxed text-[#5F6B76]">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const List = ({ title, eyebrow, items, price, testId }) => (
  <Reveal>
    <div data-testid={testId} className="h-full rounded-[4px] bg-white p-8 md:p-10">
      <p className="eyebrow">{eyebrow}</p>
      <h3 className="mt-3 font-display text-3xl font-semibold text-[#14263D] md:text-4xl">{title}</h3>
      <ul className="mt-6 space-y-3">
        {items.map((it) => <li key={it} className="flex gap-3 text-base text-[#3d4a56]"><Check className="mt-1 h-4 w-4 shrink-0 text-[#A52A32]" />{it}</li>)}
      </ul>
      <p className="mt-6 border-t border-[#14263D]/15 pt-5 font-semibold text-[#14263D]">{price}</p>
    </div>
  </Reveal>
);

const BeforeAfter = () => (
  <section data-testid="hurricane-before-after" className="bg-[#F8F7F3] py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <SectionHead eyebrow="Before & After the Storm" title={<MaskLines onView lines={["What we can do", "for your home"]} />} />
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <List testId="hurricane-before" eyebrow="Before you leave" title="Pre-storm preparation" items={HURRICANE.before} price="Departure preparation and vendor access quoted with your plan" />
        <List testId="hurricane-after" eyebrow="Once it's safe" title="Post-storm property check" items={HURRICANE.after} price="Exterior-only and interior/exterior checks quoted with your plan" />
      </div>
      <p data-testid="hurricane-limits" className="mt-8 max-w-4xl text-sm leading-relaxed text-[#5F6B76]">{HURRICANE.limits}</p>
      <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
        <TextLink to="/plans-and-pricing" testId="hurricane-pricing-link">View Home Watch Plans</TextLink>
        <TextLink to="/service-areas/wellington" testId="hurricane-wellington-link">Home watch in Wellington</TextLink>
        <TextLink to="/service-areas/royal-palm-beach" testId="hurricane-rpb-link">Home watch in Royal Palm Beach</TextLink>
      </div>
    </div>
  </section>
);

const Safety = () => (
  <section data-testid="hurricane-safety" className="bg-[#14263D] py-24 text-white md:py-32">
    <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHead light eyebrow="From the Fire Service" title={<MaskLines onView lines={["Storm safety", "tips"]} />}>
          <p>The same reminders fire crews give every storm season. They apply whether or not you're a client.</p>
        </SectionHead>
        <ClipImage src="/images/storm.jpg" alt="Storm clouds gathering over a tropical coastline with homes" className="mt-10 h-64 rounded-[4px] md:h-80" />
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:col-span-7">
        {HURRICANE.safety.map(([t, d], i) => (
          <Reveal key={t} delay={(i % 2) * 0.1}>
            <div data-testid={`hurricane-safety-${i}`} className="h-full rounded-[4px] border border-white/15 bg-white/[0.04] p-7">
              <ShieldAlert className="h-6 w-6 text-[#f0b3b7]" aria-hidden="true" />
              <h3 className="mt-4 font-display text-2xl font-semibold">{t}</h3>
              <p className="mt-3 text-base leading-relaxed text-white/85">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Hurricane() {
  usePageMeta(
    "Hurricane Season Home Watch Wellington & Royal Palm Beach FL | First Due",
    "Pre-storm preparation and post-storm home checks in Wellington, Royal Palm Beach and western Palm Beach County. Firefighter-founded. Request a personalized quote."
  );
  return (
    <main data-testid="hurricane-page">
      <PageHero testId="hurricane-hero" eyebrow="Hurricane Season Home Watch · Western Palm Beach County" lines={["Hurricane Season", "Home Watch."]} intro={HURRICANE.intro} img="/images/storm.jpg" alt="Storm clouds gathering over a tropical coastline with homes">
        <ConsultButton testId="hurricane-consult-btn" dark />
        <CallButton testId="hurricane-call-btn" dark />
      </PageHero>
      <Principles />
      <BeforeAfter />
      <Safety />
      <FaqSection items={HURRICANE.faqs} eyebrow="Storm Questions" lines={["Hurricane Season", "FAQ"]} />
      <CTABand testId="hurricane-cta" />
    </main>
  );
}
